/**
 * bn-passt-dazu.js — Web Component für theme/blocks/bn-passt-dazu.liquid
 *
 * Legt die per Checkbox ausgewählten Produkte gesammelt in den Warenkorb.
 * Der Mehrfach-Add folgt exakt dem Muster von #processBatchAddToCart in
 * assets/product-form.js (dort der Queue-Drain nach Variantenwechsel):
 * CartLinesUpdateEvent mit Deferred-Promise dispatchen, Items als JSON an
 * Theme.routes.cart_add_url schicken, danach den Warenkorb frisch laden und
 * das Promise mit Cart plus detail.sections auflösen — Cart-Drawer, Cart-Icon
 * und Cart-Items hören auf document-Ebene auf genau dieses Event und
 * aktualisieren sich selbst. product-form.js bleibt unangetastet.
 *
 * Mengen (docs/03-features.md, Feature 2): hat der Bedarfsrechner auf
 * derselben Seite ein Ergebnis, gilt dessen Bestellmenge für jede Auswahl —
 * lose gekoppelt über dataset.bestellmenge des bn-bedarfsrechner-Elements,
 * kein Import, kein globaler State (Lehre aus der data-glapor-Querkopplung
 * im Altsystem). Ohne Rechner oder ohne Ergebnis bleibt es bei Menge 1.
 *
 * Diese Menge passt fachlich nicht immer: wer 12 Eimer Putz rechnet, braucht
 * keine 12 Traufeln, und im Plattenmodus ist die Bestellmenge die Anzahl
 * PALETTEN. Solange es kein Metafeld gibt, das je Referenz sagt, ob die
 * Menge mitskaliert (Verbrauchsmaterial) oder nicht (Werkzeug), bleibt die
 * Regel aus docs/03 — sie ist aber nicht mehr unsichtbar: die Zeile über dem
 * Button nennt die Menge und lässt sie auf 1 zurücksetzen. Auch dieser
 * Schalter ist reiner DOM-Zustand (data-Attribut am Button selbst) — kein
 * Feld in der Klasse, damit er Morphs übersteht wie alles andere.
 *
 * Zustand lebt ausschließlich in den Checkboxen im DOM, Events laufen über
 * Delegation auf dem Host, connectedCallback ist idempotent
 * (AbortController) — damit ist die Komponente morph-sicher.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class BnPasstDazu extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {boolean} Anfrage läuft — Button bleibt solange gesperrt. */
  #senden = false;

  connectedCallback() {
    // Idempotent: bei erneutem connect (Morph, Editor-Reorder) alte Listener lösen.
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });

    // Die Mengenzeile liest den Rechner erst beim Nachsehen — er meldet sich
    // nicht, wenn sich sein Ergebnis ändert. Deshalb bei jeder Berührung des
    // Blocks nachziehen: Tastatur (focusin) wie Maus/Touch (pointerenter).
    this.addEventListener('focusin', this.#syncMengenzeile, { signal });
    this.addEventListener('pointerenter', this.#syncMengenzeile, { signal });

    this.#syncButton();
    this.#syncMengenzeile();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /**
   * @param {string} name - Wert des data-bn-Attributs
   * @returns {HTMLElement | null}
   */
  #teil(name) {
    return this.querySelector(`[data-bn="${name}"]`);
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.dataset.bn === 'wahl') {
      this.#syncButton();
      this.#syncMengenzeile();
    }
  };

  /** @param {Event} event */
  #handleClick = (event) => {
    const ziel = event.target instanceof Element ? event.target : null;
    if (!ziel) return;

    const schalter = ziel.closest('[data-bn="mengenschalter"]');
    if (schalter instanceof HTMLElement && this.contains(schalter)) {
      // Zustand als data-Attribut, nicht als aria-pressed: die Beschriftung
      // des Schalters wechselt mit und beschreibt die Aktion bereits — ein
      // zusätzlicher „gedrückt"-Zustand wäre für Screenreader irreführend.
      schalter.dataset.aufEins = schalter.dataset.aufEins === 'true' ? 'false' : 'true';
      this.#syncMengenzeile();
      return;
    }

    const taste = ziel.closest('[data-bn="hinzufuegen"]');
    if (taste && this.contains(taste)) {
      this.#hinzufuegen();
    }
  };

  /** @returns {HTMLInputElement[]} angehakte, nicht gesperrte Checkboxen */
  #gewaehlte() {
    return Array.from(this.querySelectorAll('input[data-bn="wahl"]:checked:not(:disabled)'));
  }

  /** Button sperren, solange nichts gewählt ist oder eine Anfrage läuft. */
  #syncButton() {
    const button = this.#teil('hinzufuegen');
    if (button instanceof HTMLButtonElement) {
      button.disabled = this.#senden || this.#gewaehlte().length === 0;
    }
  }

  /**
   * Sichtbare Fehlerzeile plus Live-Region für Screenreader.
   * Leerer Text blendet die Fehlerzeile aus.
   * @param {string} text
   */
  #meldung(text) {
    const fehler = this.#teil('fehler');
    if (fehler) {
      fehler.hidden = text === '';
      fehler.textContent = text;
    }
    this.#status(text);
  }

  /**
   * Nur die unsichtbare Live-Region (für Erfolgsmeldungen — sichtbares
   * Feedback ist der sich öffnende Cart-Drawer).
   * @param {string} text
   */
  #status(text) {
    const status = this.#teil('status');
    if (status) status.textContent = text;
  }

  /**
   * Lokalisierte Erfolgsmeldung aus den Theme-Übersetzungen
   * (content.items_added_to_cart, von snippets/scripts.liquid als
   * Theme.translations bereitgestellt).
   * @param {number} anzahl
   * @returns {string}
   */
  #erfolgsText(anzahl) {
    const uebersetzungen = /** @type {any} */ (window).Theme?.translations;
    const vorlage =
      anzahl === 1 ? uebersetzungen?.items_added_to_cart_one : uebersetzungen?.items_added_to_cart_other;
    if (typeof vorlage === 'string' && vorlage !== '') {
      return vorlage.replace('{{ count }}', String(anzahl));
    }
    return `${anzahl} Artikel zum Warenkorb hinzugefügt`;
  }

  /**
   * Warenkorb frisch laden — wie #refreshCart in product-form.js: bevorzugt
   * über die cart-items-component, sonst direkt über die AJAX-API.
   * @returns {Promise<any>}
   */
  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItemsComponent).fetchCartData();
    }

    const cartUrl = /** @type {any} */ (window).Theme?.routes?.cart_url || '/cart';
    const response = await fetch(`${cartUrl}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Bestellmenge des Bedarfsrechners auf derselben Seite, wenn der ein
   * Ergebnis hat (docs/03-features.md, Feature 2) — sonst 1. Lose Kopplung:
   * erst der eigene Abschnitt, dann die Seite, gelesen wird nur das
   * öffentliche data-Attribut, das der Rechner bei gültigem Ergebnis setzt
   * und ohne Ergebnis wieder löscht.
   * @returns {number}
   */
  #rechnermenge() {
    const bereich = this.closest('.shopify-section') || document;
    for (const wurzel of [bereich, document]) {
      for (const rechner of wurzel.querySelectorAll('bn-bedarfsrechner')) {
        if (!(rechner instanceof HTMLElement) || rechner.hidden) continue;
        const menge = parseInt(rechner.dataset.bestellmenge || '', 10);
        if (Number.isFinite(menge) && menge >= 1) return menge;
      }
    }
    return 1;
  }

  /**
   * Menge je Auswahl: die Rechnermenge, sofern der Kunde sie nicht über den
   * Schalter auf 1 zurückgesetzt hat. Der Zustand lebt im DOM (data-Attribut
   * am Schalter), es gibt keinen zweiten Speicher.
   * @returns {number}
   */
  #menge() {
    const schalter = this.#teil('mengenschalter');
    if (schalter instanceof HTMLElement && schalter.dataset.aufEins === 'true') return 1;
    return this.#rechnermenge();
  }

  /**
   * Zeile über dem Button: nennt die Menge, die jede Auswahl bekommt, und
   * bietet an, sie auf 1 zu setzen. Bei Menge 1 gibt es nichts zu erklären —
   * dann bleibt die Zeile hidden und der Schalter zurückgesetzt.
   */
  #syncMengenzeile = () => {
    const zeile = this.#teil('mengenzeile');
    const text = this.#teil('mengentext');
    const schalter = this.#teil('mengenschalter');
    if (!(zeile instanceof HTMLElement) || !(text instanceof HTMLElement)) return;
    if (!(schalter instanceof HTMLElement)) return;

    const rechnermenge = this.#rechnermenge();

    if (rechnermenge <= 1) {
      zeile.hidden = true;
      text.textContent = '';
      schalter.textContent = '';
      schalter.dataset.aufEins = 'false';
      return;
    }

    zeile.hidden = false;

    if (schalter.dataset.aufEins === 'true') {
      text.textContent = 'Menge je Auswahl: 1';
      schalter.textContent = `${rechnermenge} aus dem Bedarfsrechner übernehmen`;
    } else {
      text.textContent = `Menge je Auswahl: ${rechnermenge}, aus dem Bedarfsrechner`;
      schalter.textContent = 'Stattdessen je 1';
    }
  };

  /** Ausgewählte Produkte in einem Request in den Warenkorb legen. */
  async #hinzufuegen() {
    if (this.#senden) return;

    const menge = this.#menge();
    const zeilen = this.#gewaehlte().map((input) => ({ variantId: input.value, quantity: menge }));
    if (zeilen.length === 0) return;

    // sections-Parameter wie in product-form.js: die Section-IDs aller
    // cart-items-component mitschicken, damit die Antwort das frische
    // Drawer-HTML gleich enthält.
    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        sectionIds.push(element.dataset.sectionId);
      }
    }

    const gesamtmenge = zeilen.reduce((summe, zeile) => summe + zeile.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: zeilen.map((zeile) => ({
          merchandiseId: zeile.variantId,
          quantity: zeile.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    this.#senden = true;
    this.#syncButton();
    this.#meldung('');

    try {
      const response = await fetch(/** @type {any} */ (window).Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: zeilen.map((zeile) => ({
            id: Number(zeile.variantId),
            quantity: zeile.quantity,
          })),
          sections: sectionIds.join(','),
        }),
      });
      const daten = await response.json();

      if (daten.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: daten.message || 'Add to cart failed',
            code: 'INVALID',
            detail: {
              description: daten.description,
              errors: daten.errors,
            },
          })
        );

        this.#meldung(daten.description || daten.message || 'Das hat nicht geklappt. Bitte erneut versuchen.');

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'bn-passt-dazu',
            sourceId: this.dataset.blockId || '',
            itemCount: gesamtmenge,
          },
        });
        return;
      }

      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bn-passt-dazu',
          sourceId: this.dataset.blockId || '',
          itemCount: gesamtmenge,
          sections: daten.sections,
          didError: false,
        },
      });

      this.#status(this.#erfolgsText(gesamtmenge));
    } catch (error) {
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {any} */ (error)?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#meldung('Warenkorb gerade nicht erreichbar. Bitte erneut versuchen.');
    } finally {
      this.#senden = false;
      this.#syncButton();
    }
  }
}

if (!customElements.get('bn-passt-dazu')) {
  customElements.define('bn-passt-dazu', BnPasstDazu);
}
